import { createContext, useContext, useMemo } from 'react';
import {
  AuthProvider as DescopeProvider,
  useDescope,
  useSession,
  useUser,
} from '@descope/react-sdk';

export interface AuthUser {
  name?: string;
  email?: string;
}

export interface AuthClient {
  isAuthenticated: boolean;
  isLoading: boolean;
  user?: AuthUser;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthClient | undefined>(undefined);

export function AuthClientProvider({ children }: React.PropsWithChildren) {
  const { isAuthenticated, isSessionLoading } = useSession();
  const { isUserLoading, user } = useUser();
  const { logout } = useDescope();

  const auth = useMemo<AuthClient>(
    () => ({
      isAuthenticated,
      isLoading: isSessionLoading || (isAuthenticated && isUserLoading),
      user: isAuthenticated && user
        ? { name: user.name, email: user.email }
        : undefined,
      logout: async () => {
        await logout();
      },
    }),
    [isAuthenticated, isSessionLoading, isUserLoading, logout, user],
  );

  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const auth = useContext(AuthContext);

  if (!auth) {
    throw new Error('useAuth must be used within AuthProvider');
  }

  return auth;
}

interface AuthProviderProps {
  children: React.ReactNode;
  projectId: string;
}

const AuthProvider = ({ children, projectId }: AuthProviderProps) => (
  <DescopeProvider projectId={projectId}>
    <AuthClientProvider>{children}</AuthClientProvider>
  </DescopeProvider>
);

export default AuthProvider;
